<template>
	<ui-root v-if="loaded" :data="dynamicAttribute" :part="form" ref="frm" :errs="errors" :actions="dialogControls(env)"></ui-root>
</template>

<script setup lang="ts">
import {onMounted, onUnmounted, ref, watch, reactive} from "vue";
import {connectEventBus, injectEnv} from "@fd/Environment";
import type {DynamicAttribute} from "@t/agenda/DynamicAttribute";
import type {WithMeta} from "@t/fd/WithMeta";
import {dialogControls, FormDefinition} from "@t/fd/layout/FormDefinition";
import FormPart from "@fd/containers/FormPart.vue";
import type {ErrorMap} from "@fd/PartInjectKeys";
import {NetworkResponse} from "@fd/Types";
import {useRouter,useRoute} from "vue-router";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import UiRoot from "@fd/containers/UiRoot.vue";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";
import {getIntAttParam} from "@/router/Router.ts";
import {useVirtualRoute} from "@/router/virtual/VirtualRouter.ts";

const env = injectEnv();
const navStore = useNavigationStore();
const router = useRouter();
const route = useVirtualRoute();

const loaded = ref<boolean>(false);

const errors = reactive<ErrorMap>([]);
const dynamicAttribute = ref<DynamicAttribute|null>(null);
const form = ref<InterfaceRoot<FormDefinition>|null>(null);
const bus = connectEventBus("DynamicAttributePage");
const frm = ref(null);

function receivedData(data:DynamicAttribute & WithMeta):void {
	if (data.META) {
		form.value = data.META.form;
		delete data.META;
	}
	dynamicAttribute.value = data;
	loaded.value = true;
	if (dynamicAttribute.value && dynamicAttribute.value.id>0) {
		navStore.replace(dynamicAttribute.value.name);
	}
}

function loadData() {
	let endpoint = null;
	const id = route.query.id;
	if (id > 0) {
		endpoint = "api/dynamicattributes/" + id + "?meta=true";
	} else {
		endpoint = "api/dynamicattributes/init?meta=true&agendaCode=" + route.query.agendaCode;
	}
	return env.net.fetch(endpoint)
		.then((result) => result.json())
		.then(receivedData);
}

function saveData():Promise<NetworkResponse> {
    return env.net.fetch("api/dynamicattributes/"+dynamicAttribute.value.id, "PUT", dynamicAttribute.value);
}

function createData():Promise<NetworkResponse> {
    return env.net.fetch("api/dynamicattributes", "POST", dynamicAttribute.value);
}

function sendData():Promise<NetworkResponse> {
	return (!dynamicAttribute.value.id || dynamicAttribute.value.id===0)?createData():saveData();
}

bus.onCommand(["save", "saveAndBack"], async (cmd) => {
	errors.length = 0;
	const response = await sendData();
	if (response.ok) {
		if (cmd === "save") {
			const data = await response.json();
			receivedData(data);
			if (frm.value) {
				frm.value.setDirty(false);
			}
		} else {
			// Go back
			router.back();
		}
		return "ok";
	} else {
		const data = await response.json();
		if (Array.isArray(data?.problems)) {
			for (let problem of data.problems) {
				errors.push(problem);
			}
		}
		console.error(response);
		return "fail";
	}
});

watch(() => route.query.id, loadData, { immediate: true });
navStore.defineMenu((mb) => mb.standardMenu());

onMounted(() => {
	navStore.forward(env.msg("menu.createAttribute"));
});

</script>

<style>

</style>

